import type { AddSignee, SignatureField } from '@/types/AddSignee.ts';
import type { AutographPosition } from '@/types/AutographPosition.ts';
import type { Document } from '@/types/Document.ts';
import type { SignDocument } from '@/types/SignDocument.ts';
import type { Signee, SigneeSigning } from '@/types/Signee.ts';
import type {
  GroupSigning,
  SigneeGroup
} from '@/types/deepsign/SigneeGroup.ts';
import { updateSigneeGroupSignatureField } from '@/api/deepsign/documents-signee-groups-signature-slots.ts';

type DocumentLike = Pick<Document | SignDocument, 'files'> | null | undefined;

type SigneePositions = Pick<Signee, 'autographPosition' | 'signings'>;

/**
 * The default file a signee places signatures on for non-envelope documents:
 * the first (and usually only) file of the document.
 */
export function getDefaultSigneeFileId(document?: DocumentLike): string | null {
  return document?.files?.[0]?.fileId ?? null;
}

/**
 * All autograph positions a signee has for a given file. Falls back to the
 * legacy single `autographPosition` when no file-scoped signing exists.
 */
export function getSigneeAutographPositionsForFile(
  signee: SigneePositions,
  fileId?: string | null
): AutographPosition[] {
  if (fileId) {
    const signing = signee.signings?.find((item) => item.fileId === fileId);
    if (signing?.autographPositions?.length) {
      return signing.autographPositions;
    }
  }

  return signee.autographPosition ? [signee.autographPosition] : [];
}

/**
 * The primary (first) autograph position for a signee, preferring the given
 * file and otherwise the first signing that has a position. Used to keep the
 * legacy `autographPosition` field in sync for single-file documents.
 */
export function getPrimarySigneeAutographPosition(
  signee: SigneePositions,
  preferredFileId?: string | null
): AutographPosition | null {
  const preferredPositions = getSigneeAutographPositionsForFile(
    signee,
    preferredFileId
  );
  if (preferredPositions.length > 0) {
    return preferredPositions[0] ?? null;
  }

  const firstSigningWithPosition = signee.signings?.find(
    (signing) => signing.autographPositions.length > 0
  );

  return (
    firstSigningWithPosition?.autographPositions[0] ??
    signee.autographPosition ??
    null
  );
}

/**
 * Whether a signee has at least one placed signature, considering both the
 * legacy `autographPosition` and the file-scoped `signings`.
 */
export function hasSigneeAutographPositions(signee: SigneePositions): boolean {
  return Boolean(
    signee.autographPosition ||
    signee.signings?.some((signing) => signing.autographPositions.length > 0)
  );
}

/**
 * The authoritative multi-placeholder `signings` for a signee.
 *
 * `document.signeesOrdered` (the source of the create store's `filteredSignees`)
 * does not always carry `signings` — the authoritative data lives on
 * `document.signeeGroups[].signees[]`. Resolve from there when the signee object
 * itself lacks them so per-file placement and per-file policy checks work off
 * the real data instead of falling back to the (often stricter) signee-level
 * policy.
 */
export function resolveSigneeSignings(
  signee: Signee,
  document?: { signeeGroups?: { signees?: Signee[] }[] } | null
): SigneeSigning[] {
  if (signee.signings?.length) {
    return signee.signings;
  }

  const groups = document?.signeeGroups;
  if (groups) {
    for (const group of groups) {
      const match = group.signees?.find(
        (candidate) => candidate.signeeId === signee.signeeId
      );
      if (match?.signings?.length) {
        return match.signings;
      }
    }
  }

  return signee.signings ?? [];
}

/**
 * Whether a signee has at least one signature placed strictly on the given file.
 *
 * Reads the authoritative `signings` (resolving from the document's signee
 * groups when the signee object omits them). Does NOT fall back to the legacy
 * document-wide `autographPosition` for arbitrary files — that would report a
 * false placement on files the signee never signed. The legacy field is only
 * honoured for the document's default file (single-file documents).
 */
export function hasSigneePlacementOnFile(
  signee: Signee,
  fileId: string | null,
  document?:
    | (Pick<Document | SignDocument, 'files'> & {
        signeeGroups?: { signees?: Signee[] }[];
      })
    | null
): boolean {
  const signings = resolveSigneeSignings(signee, document);
  if (fileId && signings.length) {
    const signing = signings.find((item) => item.fileId === fileId);
    return Boolean(signing && signing.autographPositions.length > 0);
  }

  const isDefaultFile = !fileId || fileId === getDefaultSigneeFileId(document);
  return isDefaultFile ? Boolean(signee.autographPosition) : false;
}

/**
 * Build the local `signings` array from an AddSignee payload, honouring the
 * newer `fileSignatureFields` map and falling back to the single
 * `autographPosition` for backward compatibility.
 */
export function buildSigningsFromAddSignee(
  payload: AddSignee,
  defaultFileId?: string | null,
  existingSignings: SigneeSigning[] = []
): SigneeSigning[] {
  if (payload.fileSignatureFields) {
    return Object.entries(payload.fileSignatureFields)
      .filter(([, field]) => (field?.autographPositions?.length ?? 0) > 0)
      .map(([fileId, field]) => ({
        fileId,
        autographPositions: [...(field.autographPositions ?? [])],
        policy:
          existingSignings.find((signing) => signing.fileId === fileId)
            ?.policy ?? {}
      }));
  }

  if (payload.autographPosition && defaultFileId) {
    return [
      {
        fileId: defaultFileId,
        autographPositions: [payload.autographPosition],
        policy:
          existingSignings.find((signing) => signing.fileId === defaultFileId)
            ?.policy ?? {}
      }
    ];
  }

  return existingSignings;
}

/**
 * Convert local `signings` into the `fileSignatureFields` map used by the
 * AddSignee create payload.
 */
export function buildFileSignatureFieldsFromSignings(
  signings?: SigneeSigning[] | null
): Record<string, SignatureField> | undefined {
  if (!signings?.length) return undefined;

  const result: Record<string, SignatureField> = {};

  signings.forEach((signing) => {
    if (signing.autographPositions.length === 0) return;
    result[signing.fileId] = {
      autographPositions: [...signing.autographPositions]
    };
  });

  return Object.keys(result).length > 0 ? result : undefined;
}

/**
 * Convert local `signings` into the `fileAutographPositions` map used by the
 * UpdateSignee payload. Always includes every known file so empty arrays clear
 * positions for that file on the backend.
 */
export function buildFileAutographPositionsFromSignings(
  signings?: SigneeSigning[] | null
): Record<string, AutographPosition[]> | undefined {
  if (!signings?.length) return undefined;

  const result: Record<string, AutographPosition[]> = {};

  signings.forEach((signing) => {
    result[signing.fileId] = [...signing.autographPositions];
  });

  return Object.keys(result).length > 0 ? result : undefined;
}

/**
 * Merge a `fileAutographPositions` map into the existing `signings`, replacing
 * the positions for the listed files while preserving untouched signings.
 */
export function applyFileAutographPositionsToSignings(
  currentSignings: SigneeSigning[] = [],
  fileAutographPositions?: Record<string, AutographPosition[]> | null
): SigneeSigning[] {
  if (fileAutographPositions === undefined || fileAutographPositions === null) {
    return currentSignings;
  }

  const byFileId = new Map(
    currentSignings.map((signing) => [signing.fileId, signing])
  );

  Object.entries(fileAutographPositions).forEach(([fileId, positions]) => {
    byFileId.set(fileId, {
      fileId,
      autographPositions: [...positions],
      policy: byFileId.get(fileId)?.policy ?? {}
    });
  });

  return [...byFileId.values()];
}

/**
 * The envelope signee group a signee belongs to, looked up by signeeId.
 */
export function findSigneeGroupForSignee(
  signeeId: string,
  signeeGroups?: SigneeGroup[] | null
): SigneeGroup | undefined {
  return signeeGroups?.find((group) =>
    group.signees?.some((signee) => signee.signeeId === signeeId)
  );
}

/**
 * The envelope signee group looked up by its own id, rather than by a
 * member's signeeId. Used once a placeholder's identity is itself group-owned
 * (see `getQuorumSlotPlaceholderId`) and no member signeeId is available/needed.
 */
export function findSigneeGroupById(
  signeeGroupId: string,
  signeeGroups?: SigneeGroup[] | null
): SigneeGroup | undefined {
  return signeeGroups?.find((group) => group.signeeGroupId === signeeGroupId);
}

/**
 * Whether a signee group has a quorum (`requiredSignatures > 0`) set. Once a
 * group has a quorum, its members' autograph positions are shared, group-owned
 * signature slots rather than per-signee data — `PUT /signees/{signeeId}` with
 * `autographPosition` / `fileAutographPositions` then 403s.
 */
export function isSigneeGroupQuorumSet(
  signeeGroup?: SigneeGroup | null
): boolean {
  return Boolean(
    signeeGroup?.requiredSignatures && signeeGroup.requiredSignatures > 0
  );
}

/**
 * Whether `signee` is authorized to place/move/resize/delete their autograph
 * position on `fileId`, combining every policy source that can grant it:
 * the document-level policy, the signee-level policy, and — since autograph
 * editability can be granted per file — that file's own signing policy (see
 * the `effectivePolicy` built in `usePlacement.ts`'s `getPlaceHolderData`).
 * Always `false` for a quorum-set signee group (`isSigneeGroupQuorumSet`):
 * such a group's shared slot is never self-placeable by an individual member
 * regardless of any of these policies — it's either pre-placed by the
 * initiator during document creation, or not placeable here at all.
 */
export function canSigneeModifyAutographPositionOnFile(
  signee: Signee,
  fileId: string | null | undefined,
  document?:
    | (Pick<Document | SignDocument, 'files' | 'documentPolicy'> & {
        signeeGroups?: SigneeGroup[];
      })
    | null
): boolean {
  const signeeGroup = findSigneeGroupForSignee(
    signee.signeeId,
    document?.signeeGroups
  );
  if (isSigneeGroupQuorumSet(signeeGroup)) return false;

  if (document?.documentPolicy?.canModifyAutographPosition) return true;
  if (signee.policy?.canModifyAutographPosition) return true;
  if (!fileId) return false;

  const signingForFile = resolveSigneeSignings(signee, document).find(
    (item) => item.fileId === fileId
  );
  return Boolean(signingForFile?.policy?.canModifyAutographPosition);
}

/**
 * The signee's fixed, stable "slot" within a quorum-enabled group: its ordinal
 * position in `signeeGroup.signees`. Quorum members share group-owned
 * signature slots (see `isSigneeGroupQuorumSet`) rather than owning their own
 * per-signee positions, so every member needs a single, collision-free
 * `signatureSlot` to read/write against — one that doesn't depend on how many
 * positions any particular member has placed locally. The member's index in
 * the group is the only stable value the backend gives us for this, so it
 * doubles as that shared slot number on both the read (rendering group
 * `signings`) and write (`updateSigneeGroupSignatureField`) paths.
 */
export function getSigneeGroupSlotIndex(
  signeeGroup: SigneeGroup,
  signeeId: string
): number {
  const index =
    signeeGroup.signees?.findIndex((signee) => signee.signeeId === signeeId) ??
    -1;
  return index >= 0 ? index : 0;
}

/**
 * Deterministic, group-owned identity for a quorum slot placeholder. Unlike an
 * ordinary signee's placeholder `id` (the signee's own `signeeId`), a slot
 * belongs to the signee group as a shared data structure — nobody's personal
 * id/email should ever end up on it — so it gets its own id derived purely
 * from the group and the slot's index instead.
 */
export function getQuorumSlotPlaceholderId(
  signeeGroupId: string,
  signatureSlot: number
): string {
  return `quorum-slot-${signeeGroupId}-${signatureSlot}`;
}

/**
 * Whether `placeholder` is the specific quorum slot (or ordinary signee
 * placeholder) that `signeeId` should read/interact with. An ordinary
 * placeholder's `id` IS the owning signee's id, so it's a direct match. A
 * quorum slot's `id` is group-owned (see `getQuorumSlotPlaceholderId`), not
 * any member's id, so matching it instead resolves `signeeId`'s own fixed
 * slot index within its group (`getSigneeGroupSlotIndex`) and compares that
 * against the placeholder's `signatureSlot` — the same fixed mapping used to
 * generate the slot in the first place (see `usePlacement`'s
 * `getQuorumGroupSlotPlaceholders`).
 */
export function isPlaceholderForSignee(
  placeholder: {
    id: string;
    isQuorumSlot?: boolean;
    signeeGroupId?: string;
    signatureSlot?: number;
  },
  signeeId: string,
  signeeGroups?: SigneeGroup[] | null
): boolean {
  if (!placeholder.isQuorumSlot) {
    return placeholder.id === signeeId;
  }
  const signeeGroup = findSigneeGroupForSignee(signeeId, signeeGroups);
  if (!signeeGroup || signeeGroup.signeeGroupId !== placeholder.signeeGroupId) {
    return false;
  }
  return (
    getSigneeGroupSlotIndex(signeeGroup, signeeId) ===
    (placeholder.signatureSlot ?? -1)
  );
}

/**
 * The group-owned signing entry (shared slot) for a given file and slot
 * index, read from `signeeGroup.signings`. `signedSigneeId` is `null` until a
 * member actually signs it — any member may place/move the slot's position
 * until then.
 */
export function findGroupSigningForSlot(
  signeeGroup: SigneeGroup,
  fileId: string,
  signatureSlot: number
): GroupSigning | undefined {
  return signeeGroup.signings?.find(
    (signing) =>
      signing.fileId === fileId && signing.signatureSlot === signatureSlot
  );
}

/**
 * Which of a quorum group's shared slots `signeeId` is actually going to fill
 * on `fileId`, or `null` if none is up for grabs.
 *
 * A quorum group's slots are group-owned and anonymous until signed — nothing
 * assigns a member to a particular one up front, quorum is satisfied by
 * whichever members sign first (see the "{required} out of {total} persons
 * must sign" copy). So a member's slot is simply the lowest-numbered one
 * still open (`signedSigneeId === null`), or — once they've signed — the one
 * they already took.
 *
 * Only slots the initiator actually placed a position on for this file are
 * considered: an unplaced slot renders no placeholder (see `usePlacement`'s
 * `getQuorumGroupSlotPlaceholders`), so letting it claim the "first open"
 * spot would point the signee at nothing.
 *
 * NOT interchangeable with `getSigneeGroupSlotIndex` / `isPlaceholderForSignee`:
 * those map a member to a slot by their FIXED ordinal within the group, which
 * is the right identity while the initiator builds the document (each slot
 * card owns its own placeholder), but on the sign step it would mark the wrong
 * slot as "yours" and leave every overflow member (ordinal >=
 * `requiredSignatures`) matching no slot at all.
 */
export function getQuorumSlotForSignee(
  signeeGroup: SigneeGroup | null | undefined,
  signeeId: string,
  fileId: string | null | undefined
): number | null {
  if (!isSigneeGroupQuorumSet(signeeGroup) || !fileId) return null;
  const isMember = signeeGroup!.signees?.some(
    (signee) => signee.signeeId === signeeId
  );
  if (!isMember) return null;

  const placedSlots = (signeeGroup!.signings ?? [])
    .filter(
      (signing) =>
        signing.fileId === fileId &&
        (signing.autographPositions?.length ?? 0) > 0
    )
    .sort((a, b) => a.signatureSlot - b.signatureSlot);

  const ownSlot = placedSlots.find(
    (signing) => signing.signedSigneeId === signeeId
  );
  if (ownSlot) return ownSlot.signatureSlot;

  const openSlot = placedSlots.find((signing) => !signing.signedSigneeId);
  return openSlot ? openSlot.signatureSlot : null;
}

/**
 * Whether `placeholder` is the quorum slot `signeeId` is going to sign (see
 * `getQuorumSlotForSignee`). Returns `false` for anything that isn't a quorum
 * slot of the signee's own group.
 */
export function isQuorumSlotForSignee(
  placeholder: {
    isQuorumSlot?: boolean;
    signeeGroupId?: string;
    signatureSlot?: number;
    fileId?: string;
  },
  signeeId: string | null | undefined,
  signeeGroups?: SigneeGroup[] | null
): boolean {
  if (!placeholder.isQuorumSlot || !signeeId) return false;
  const signeeGroup = findSigneeGroupForSignee(signeeId, signeeGroups);
  if (!signeeGroup || signeeGroup.signeeGroupId !== placeholder.signeeGroupId) {
    return false;
  }
  return (
    getQuorumSlotForSignee(signeeGroup, signeeId, placeholder.fileId) ===
    placeholder.signatureSlot
  );
}

/**
 * Whether a signee's obligation to place a visual signature on the given file
 * is satisfied, for "has everyone placed" completeness checks (e.g. the
 * "place all signatures" switch, or the files-missing-a-signature list).
 *
 * Quorum-set groups (see `isSigneeGroupQuorumSet`) share group-owned signature
 * slots: only the first `requiredSignatures` members (by their fixed group
 * ordinal, see `getSigneeGroupSlotIndex`) own a slot at all — the remaining
 * members own nothing, can never place anything themselves, and must be
 * treated as automatically satisfied so they don't block completeness. A
 * slot-owning member is satisfied once THEIR OWN slot carries an autograph
 * position for the file — reading their own `signings` (as
 * `hasSigneePlacementOnFile` does) would never reflect this, since quorum
 * placements live on the group, not the member.
 */
export function isSigneePlacementSatisfiedOnFile(
  signee: Signee,
  fileId: string | null,
  document?:
    | (Pick<Document | SignDocument, 'files'> & {
        signeeGroups?: SigneeGroup[];
      })
    | null
): boolean {
  const signeeGroup = findSigneeGroupForSignee(
    signee.signeeId,
    document?.signeeGroups
  );

  if (isSigneeGroupQuorumSet(signeeGroup)) {
    const slot = getSigneeGroupSlotIndex(signeeGroup!, signee.signeeId);
    if (slot >= (signeeGroup!.requiredSignatures ?? 0)) return true;
    if (!fileId) return false;
    const groupSigning = findGroupSigningForSlot(signeeGroup!, fileId, slot);
    return (groupSigning?.autographPositions?.length ?? 0) > 0;
  }

  return hasSigneePlacementOnFile(signee, fileId, document);
}

/**
 * Mutates `signeeGroup.signings` in place to reflect a just-persisted
 * position for the group's fixed `signatureSlot`, so local state (and
 * anything deriving from it — e.g. the sidebar's placed/locked checks, or
 * re-rendered placeholders) reflects the change immediately without waiting
 * for a full reload. `signedSigneeId` is preserved as-is — placing/moving a
 * position never fulfills the quorum by itself, only actually signing does.
 */
export function applyGroupSignatureFieldsLocally(
  signeeGroup: SigneeGroup,
  signatureSlot: number,
  fileAutographPositions: Record<string, AutographPosition[]>
): void {
  const signings = signeeGroup.signings ? [...signeeGroup.signings] : [];

  for (const [fileId, positions] of Object.entries(fileAutographPositions)) {
    const index = signings.findIndex(
      (signing) =>
        signing.fileId === fileId && signing.signatureSlot === signatureSlot
    );
    const existing = index >= 0 ? signings[index] : undefined;
    const updated: GroupSigning = {
      fileId,
      signatureSlot,
      autographPositions: positions,
      signedSigneeId: existing?.signedSigneeId ?? null
    };

    if (index >= 0) {
      signings[index] = updated;
    } else {
      signings.push(updated);
    }
  }

  signeeGroup.signings = signings;
}

/**
 * Persists autograph positions for a quorum-enabled signee group via
 * `PUT /documents/{documentId}/signee-groups/{signeeGroupId}/files/{fileId}/signature-slots/{signatureSlot}`
 * instead of the signee endpoint. The signature slot is the signee's fixed,
 * group-wide slot index (`getSigneeGroupSlotIndex`) — NOT a position's index
 * within the signee's own local positions array, which would collide across
 * members (every member's own array starts at index 0). Each quorum member
 * owns exactly one shared slot, so only the first position for each file is
 * persisted.
 */
export async function persistSigneeGroupSignatureFields(
  documentId: string,
  signeeGroup: SigneeGroup,
  signeeId: string,
  fileAutographPositions: Record<string, AutographPosition[]>
): Promise<void> {
  const signatureSlot = getSigneeGroupSlotIndex(signeeGroup, signeeId);
  const persistedFileAutographPositions: Record<string, AutographPosition[]> =
    {};

  for (const [fileId, positions] of Object.entries(fileAutographPositions)) {
    const position = positions[0] ?? null;
    persistedFileAutographPositions[fileId] = position ? [position] : [];
    await updateSigneeGroupSignatureField(
      documentId,
      signeeGroup.signeeGroupId,
      fileId,
      signatureSlot,
      { autographPositions: position ? [position] : [] }
    );
  }

  applyGroupSignatureFieldsLocally(
    signeeGroup,
    signatureSlot,
    persistedFileAutographPositions
  );
}

/**
 * Persists a manually dragged/resized/removed quorum slot placeholder
 * directly via `updateSigneeGroupSignatureField` — never through the signee
 * endpoint. Takes the group's own `signatureSlot` directly rather than a
 * signeeId: the slot placeholder is a group-owned data structure (see
 * `getQuorumSlotPlaceholderId`), so once it already exists there's no need to
 * resolve it through any particular member.
 */
export async function persistQuorumSlotPlaceholder(
  documentId: string,
  signeeGroup: SigneeGroup,
  signatureSlot: number,
  fileId: string,
  position: AutographPosition | null
): Promise<void> {
  await updateSigneeGroupSignatureField(
    documentId,
    signeeGroup.signeeGroupId,
    fileId,
    signatureSlot,
    { autographPositions: position ? [position] : [] }
  );

  applyGroupSignatureFieldsLocally(signeeGroup, signatureSlot, {
    [fileId]: position ? [position] : []
  });
}

/**
 * Resolves the `fileAutographPositions` map to persist for a quorum group from
 * an `UpdateSignee`-shaped payload, falling back to the legacy single
 * `autographPosition` field scoped to the signee's default file.
 */
export function resolveFileAutographPositionsForGroup(
  payload: {
    autographPosition?: AutographPosition | null;
    fileAutographPositions?: Record<string, AutographPosition[]> | null;
  },
  defaultFileId?: string | null
): Record<string, AutographPosition[]> {
  if (payload.fileAutographPositions) {
    return payload.fileAutographPositions;
  }
  if (payload.autographPosition !== undefined && defaultFileId) {
    return {
      [defaultFileId]: payload.autographPosition
        ? [payload.autographPosition]
        : []
    };
  }
  return {};
}
